import React, { useMemo } from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import CommunityPositioningLayout from 'common/Widgets/Graph/CommunityPositioningLayout';
import { useWidgetDataFetcher } from 'hooks/useWidgetDataFetcher';
import { useAppSelector } from 'store/hook';

import MarketPositioningCommunityEntry from './CommunityEntry';
import MarketPositioningChart from './MarketPositioningChart';
import { MarketPositioningFilters, MarketPositioningPoint, XAxisSource } from './types';
import { WIDGET_TYPES } from '../constants';
import { calculateValueDifference, shouldDisplayValueDifference } from '../valueDifferenceUtils';
import { DATA_RANGE_TYPE } from '../Widget/constants';
import NoDataInfo from '../Widget/NoDataContent';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100%;
  min-height: 0;
  overflow: hidden;

  &.modal-preview {
    height: 500px;
    width: 1000px;
    overflow: unset;
  }
`;

const Content = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding-top: 20px;
`;

const EmptyHint = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

type Props = {
  communityId: number;
  filters: MarketPositioningFilters;
  xAxis: XAxisSource;
  widgetType?: number;
  competitorOverride?: number | null;
  publicHash?: string;
  isModalPreview?: boolean;
  useScoreFormat?: boolean;
};

const MarketPositioningWidgetContent = ({
  communityId,
  filters,
  xAxis,
  widgetType = WIDGET_TYPES.MARKET_POSITIONING,
  competitorOverride = null,
  publicHash,
  isModalPreview = false,
  useScoreFormat = false,
}: Props) => {
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const { fetchWidgetData } = useWidgetDataFetcher();

  const {
    competitors,
    careTypes,
    roomTypes,
    roomTypesPriceDisplay,
    careLevels,
    reviewSources,
    reviewCategories,
    swotCategories,
    communityContractType,
    communityContractSelectionType,
    contractTypes,
    comparisonTypeEnabled,
    valueDisplay,
  } = filters;

  const showDifference = shouldDisplayValueDifference(comparisonTypeEnabled, valueDisplay);

  const effectiveCompetitors = competitorOverride !== null ? [competitorOverride] : competitors;
  const shouldFetch = communityId > 0;

  const { data: points = [], isLoading } = useQuery<MarketPositioningPoint[]>({
    queryKey: ['market-positioning', widgetType, communityId, filters, competitorOverride, publicHash],
    queryFn: () =>
      fetchWidgetData(communityId, widgetType, DATA_RANGE_TYPE.NOW, {
        filters: {
          competitors: effectiveCompetitors,
          care_types: careTypes,
          room_types: roomTypes,
          room_types_price_display: roomTypesPriceDisplay,
          care_levels: careLevels,
          review_sources: reviewSources,
          review_categories: reviewCategories,
          swot_categories: swotCategories,
          community_contract_type: communityContractType,
          community_contract_selection_type: communityContractSelectionType,
          contract_types: contractTypes,
        } as any,
        ...(publicHash ? { public_hash: publicHash } : {}),
      }).then((response: MarketPositioningPoint[]) => response ?? []),
    refetchOnWindowFocus: false,
    enabled: shouldFetch,
  });

  const selectedCommunityId = selectedCommunity?.id ?? null;

  const displayPoints = useMemo(
    () =>
      points
        .map((point) => {
          const value = xAxis.getValue(point);
          if (value === undefined) return null;
          return { ...point, quality_score: value };
        })
        .filter((point): point is MarketPositioningPoint => point !== null),
    [points, xAxis]
  );

  const community = useMemo(
    () => displayPoints.find((point) => selectedCommunityId !== null && point.community_id === selectedCommunityId),
    [displayPoints, selectedCommunityId]
  );
  const competitorsList = useMemo(() => {
    const competitorsOnly =
      selectedCommunityId !== null
        ? displayPoints.filter((point) => point.community_id !== selectedCommunityId)
        : displayPoints;
    const sorted = competitorsOnly.sort((a, b) => b.quality_score - a.quality_score);
    if (!showDifference || !community) return sorted;
    return sorted.map((competitor) => ({
      ...competitor,
      rateDifference: calculateValueDifference(
        community.market_rate,
        competitor.market_rate,
        valueDisplay,
        WIDGET_TYPES.MARKET_RATE_GRAPH
      ),
      qualityDifference: calculateValueDifference(
        community.quality_score,
        competitor.quality_score,
        valueDisplay,
        WIDGET_TYPES.RATING_GRAPH
      ),
    }));
  }, [displayPoints, selectedCommunityId, showDifference, community, valueDisplay]);

  if (!isLoading && displayPoints.length === 0) {
    return (
      <Container className={isModalPreview ? 'modal-preview' : ''}>
        <NoDataInfo>No data available</NoDataInfo>
      </Container>
    );
  }

  return (
    <Container className={isModalPreview ? 'modal-preview' : ''}>
      {isLoading && <NoDataInfo>Loading...</NoDataInfo>}
      {!isLoading && displayPoints.length > 0 && (
        <Content>
          <CommunityPositioningLayout
            chart={
              <MarketPositioningChart
                data={displayPoints}
                showDifference={showDifference}
                valueDisplay={valueDisplay}
                community={community}
                roomTypesPriceDisplay={roomTypesPriceDisplay}
                xAxisTitle={xAxis.title}
                useScoreFormat={useScoreFormat}
              />
            }
            primaryEntry={
              community ? (
                <MarketPositioningCommunityEntry
                  community={community}
                  roomTypesPriceDisplay={roomTypesPriceDisplay}
                  useScoreFormat={useScoreFormat}
                />
              ) : (
                <EmptyHint>No community data</EmptyHint>
              )
            }
            competitorEntries={
              competitorsList.length > 0
                ? competitorsList.map((competitor: MarketPositioningPoint) => (
                    <MarketPositioningCommunityEntry
                      key={competitor.community_id}
                      community={competitor}
                      valueDisplay={valueDisplay}
                      roomTypesPriceDisplay={roomTypesPriceDisplay}
                      useScoreFormat={useScoreFormat}
                    />
                  ))
                : [<EmptyHint key="no-competitors">No competitors data</EmptyHint>]
            }
            scrollSidebar={!isModalPreview}
          />
        </Content>
      )}
    </Container>
  );
};

export default MarketPositioningWidgetContent;
